import { useMutation } from '@apollo/client'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { isNil } from 'lodash'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Loader from '../../../common/loader/Loader'
import { Campaign } from '../../../common/types/campaigns'
import { Query } from '../../../react-apollo/Components'
import authStore from '../../auth/authStore'
import { User } from '../../auth/types'
import { i18n } from '../../index/index'
import {
  addNewCampaignMutation,
  addNewStationCampaignMutation,
  fetchAdminCampaigns,
  updateCampaignMutation,
  updateStationCampaignMutation,
} from './campaignQueries'

export default function CampaignsPage() {
  const { stationId } = useParams<{ stationId?: string }>()
  const now = moment().toISOString()

  return (
    <Container sx={{ marginTop: '80px' }}>
      <Typography variant='h4'>{i18n.t('campaigns.heading')}</Typography>
      <Query<
        { campaigns?: Campaign[]; campaignsAfterDate?: Campaign[] },
        { date: string; stationId?: string; includeChainCampaigns?: boolean }
      >
        query={fetchAdminCampaigns}
        variables={{ date: now, stationId, includeChainCampaigns: true }}
      >
        {({ data, loading, refetch }) => {
          return (
            <Loader loading={loading}>
              <NewCampaignForm stationId={stationId} refetch={refetch} />
              <Typography variant='h6'>{i18n.t('campaigns.currentCampaigns')}</Typography>
              <CampaignTable campaigns={data?.campaigns ?? []} stationId={stationId} user={authStore.user} />
              <Typography variant='h6'>{i18n.t('campaigns.futureCampaigns')}</Typography>
              <CampaignTable campaigns={data?.campaignsAfterDate ?? []} stationId={stationId} user={authStore.user} />
            </Loader>
          )
        }}
      </Query>
    </Container>
  )
}
function CampaignTable({
  campaigns,
  stationId,
  user,
}: {
  campaigns: Campaign[]
  stationId?: string
  user?: User | null
}) {
  const [campaignToEdit, setCampaignToEdit] = useState<Campaign | null>(null)
  const [isEditCampaignOpen, setIsEditCampaignOpen] = useState(false)

  function onCloseEditCampaign() {
    setIsEditCampaignOpen(false)
  }

  function openEditCampaign(campaign: Campaign) {
    setCampaignToEdit(campaign)
    setIsEditCampaignOpen(true)
  }

  function canEditCampaign(campaign: Campaign, user: User | null | undefined) {
    if (isNil(user)) {
      return false
    }
    if (!campaign.stationId) {
      return user.permissions.includes('manage_chain')
    }
    if (campaign.stationId === stationId) {
      return user.permissions.includes('manage_ownStation')
    }
    return false
  }

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t('campaigns.name')}</TableCell>
            <TableCell>{i18n.t('campaigns.starts')}</TableCell>
            <TableCell>{i18n.t('campaigns.ends')}</TableCell>
            <TableCell>{i18n.t('campaigns.description')}</TableCell>
            <TableCell>{i18n.t('buttons.edit')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(campaigns ?? []).map((campaign: Campaign) => (
            <TableRow key={campaign.id}>
              <TableCell>{campaign.name}</TableCell>
              <TableCell>{moment(campaign.startTime).format('D.M.YYYY')}</TableCell>
              <TableCell>{moment(campaign.endTime).format('D.M.YYYY')}</TableCell>
              <TableCell>{campaign.description}</TableCell>
              <TableCell>
                <IconButton
                  size='small'
                  onClick={() => openEditCampaign(campaign)}
                  disabled={!canEditCampaign(campaign, user)}
                >
                  <EditIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EditCampaignForm
        campaign={campaignToEdit}
        onClose={onCloseEditCampaign}
        open={isEditCampaignOpen}
        stationId={stationId}
      />
    </Paper>
  )
}

function NewCampaignForm({ stationId, refetch }: { stationId?: string; refetch: (...args: Array<any>) => any }) {
  const [name, setName] = useState('')
  const [starts, setStarts] = useState<moment.Moment | null>(null)
  const [ends, setEnds] = useState<moment.Moment | null>(null)
  const [description, setDescription] = useState('')

  const [addCampaign, result] = useMutation(stationId ? addNewStationCampaignMutation : addNewCampaignMutation)

  function onNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value)
  }

  function onDescriptionChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDescription(e.target.value)
  }

  function resetForm() {
    setName('')
    setStarts(null)
    setEnds(null)
    setDescription('')
  }

  function onSubmit() {
    addCampaign({
      variables: {
        stationId,
        input: {
          name,
          startTime: starts?.toISOString(),
          endTime: ends?.toISOString(),
          description: description,
        },
      },
    }).then(() => {
      resetForm()
      refetch()
    })
  }

  return (
    <Box>
      <Typography variant='h6'>Lisää uusi kampanja</Typography>
      <Paper sx={{ padding: 2 }}>
        <Stack spacing={2} direction='column'>
          <Stack direction='row'>
            <TextField
              label={i18n.t('campaigns.name')}
              variant='outlined'
              onChange={onNameChange}
              value={name}
              fullWidth
            />
          </Stack>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack spacing={2} direction='row'>
              <DatePicker label={i18n.t('campaigns.starts')} value={starts} onChange={setStarts} />
              <DatePicker label={i18n.t('campaigns.ends')} value={ends} onChange={setEnds} />
            </Stack>
          </LocalizationProvider>
          <Stack direction={'row'}>
            <TextField
              label={i18n.t('campaigns.description')}
              variant='outlined'
              value={description}
              onChange={onDescriptionChange}
              fullWidth
              multiline
              rows={5}
            />
          </Stack>
          <Stack direction='row' justifyContent={'flex-end'}>
            <Button variant='contained' color='primary' disabled={!name || !starts || !ends} onClick={onSubmit}>
              {i18n.t('buttons.save')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  )
}

function EditCampaignForm({
  campaign,
  onClose,
  open,
  stationId,
}: {
  campaign: Campaign | null
  onClose: () => void
  open: boolean
  stationId?: string
}) {
  const [name, setName] = useState(campaign?.name)
  const [starts, setStarts] = useState<moment.Moment | null>(moment(campaign?.startTime))
  const [ends, setEnds] = useState<moment.Moment | null>(moment(campaign?.endTime))
  const [description, setDescription] = useState(campaign?.description ?? '')

  const [updateCampaign, result] = useMutation(stationId ? updateStationCampaignMutation : updateCampaignMutation)

  function onNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value)
  }

  function onDescriptionChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDescription(e.target.value)
  }

  function onSubmit() {
    updateCampaign({
      variables: {
        id: campaign!.id,
        stationId,
        input: {
          name,
          startTime: starts?.toISOString(),
          endTime: ends?.toISOString(),
          description,
        },
      },
    }).then(() => {
      onClose()
    })
  }

  useEffect(() => {
    setName(campaign?.name)
    setStarts(moment(campaign?.startTime))
    setEnds(moment(campaign?.endTime))
    setDescription(campaign?.description ?? '')
  }, [campaign])

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <Box>
          <Typography variant='h6'>{i18n.t('campaigns.editCampaign')}</Typography>
          <Paper sx={{ padding: 2 }}>
            <Stack spacing={2} direction='column'>
              <Stack direction='row'>
                <TextField
                  label={i18n.t('campaigns.name')}
                  variant='outlined'
                  onChange={onNameChange}
                  value={name}
                  fullWidth
                />
              </Stack>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Stack spacing={2} direction='row'>
                  <DatePicker label={i18n.t('campaigns.starts')} value={starts} onChange={setStarts} />
                  <DatePicker label={i18n.t('campaigns.ends')} value={ends} onChange={setEnds} />
                </Stack>
              </LocalizationProvider>
              <Stack direction={'row'}>
                <TextField
                  label={i18n.t('campaigns.description')}
                  variant='outlined'
                  value={description}
                  onChange={onDescriptionChange}
                  fullWidth
                  multiline
                  rows={5}
                />
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={onClose}>
          {i18n.t('buttons.cancel')}
        </Button>
        <Button variant='contained' color='primary' onClick={onSubmit}>
          {i18n.t('buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
